<template>
  <div class="audit-item" :class="{ 'is-expanded': isExpanded }">
    <!-- User Avatar -->
    <div class="audit-avatar">
      <v-avatar size="32" :color="avatarColor" class="avatar-circle">
        <v-img v-if="userAvatarUrl" :src="userAvatarUrl" />
        <span v-else class="avatar-text">{{ userInitials }}</span>
      </v-avatar>
    </div>

    <!-- Content -->
    <div class="audit-body">
      <!-- Header: User + Action -->
      <div class="audit-header">
        <span class="audit-user">{{ userName }}</span>
        <span class="audit-action">{{ actionDescription }}</span>
      </div>

      <!-- Timestamp -->
      <time class="audit-time">{{ formattedTimestamp }}</time>

      <!-- Field Names List (collapsed view - show field tags) -->
      <div v-if="showFieldsList && !isExpanded" class="audit-fields-list">
        <span
          v-for="(fieldName, idx) in changedFieldNames"
          :key="idx"
          class="field-tag">
          {{ fieldName }}
        </span>
      </div>

      <!-- Expand/Collapse Button -->
      <button
        v-if="canViewDetails"
        class="audit-details-btn"
        @click.stop="toggleExpand">
        <span>{{ isExpanded ? 'Hide changes' : 'View all changes' }}</span>
        <v-progress-circular
          v-if="loadingDetail"
          indeterminate
          size="12"
          width="2"
          color="primary" />
        <svg v-else :class="{ 'rotated': isExpanded }" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Expanded: Detailed Changes (diff-style) -->
      <transition name="expand">
        <div v-if="isExpanded && detailedChanges.length > 0" class="audit-detail-expanded">
          <div
            v-for="(change, idx) in detailedChanges"
            :key="idx"
            class="change-card">
            <div class="change-field">
              <span class="field-name">{{ change.displayName || change.fieldName }}</span>
              <span v-if="change.dataType && change.dataType !== 'text'" class="field-type">{{ change.dataType }}</span>
            </div>
            <div class="change-values">
              <div class="value-row value-old">
                <span class="value-indicator">−</span>
                <span class="value-content">{{ formatValue(change.oldValue) }}</span>
              </div>
              <div class="value-row value-new">
                <span class="value-indicator">+</span>
                <span class="value-content">{{ formatValue(change.newValue) }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Expanded but no changes -->
      <div v-if="isExpanded && detailedChanges.length === 0 && !loadingDetail" class="no-changes-msg">
        <span>No detailed changes recorded</span>
      </div>
    </div>
  </div>
</template>

<script>
import registerService from '../../services/registerService';
import { log, warn, error } from '@/utils/debugLogger';

export default {
  name: 'AuditLogItem',
  props: {
    log: {
      type: Object,
      required: true
    },
    isLast: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maxDisplayFields: 4,
      isExpanded: false,
      loadingDetail: false,
      detailedChanges: []
    };
  },
  computed: {
    // User data from nested API response
    userData() {
      return this.log.user || {};
    },
    userName() {
      if (this.userData.displayName) return this.userData.displayName;
      if (this.userData.firstName && this.userData.lastName) {
        return `${this.userData.firstName} ${this.userData.lastName}`;
      }
      return this.log.userName || 'System';
    },
    userAvatarUrl() {
      return this.userData.avatarUrl || this.log.userAvatar || null;
    },
    userInitials() {
      const name = this.userName;
      if (!name || name === 'System') return 'SY';
      const parts = name.split(' ').filter(p => p.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
    avatarColor() {
      // Warm, Jira-like colors based on user name hash
      const colors = [
        '#F59E0B', '#F97316', '#EF4444', '#EC4899',
        '#8B5CF6', '#3B82F6', '#06B6D4', '#10B981'
      ];
      const name = this.userName;
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },

    // Natural language action description
    actionDescription() {
      const actionType = this.log.actionType;
      const actionCategory = this.log.actionCategory;
      const count = this.log.changeCount || this.log.changes?.length || 0;
      const isRegisterLevel = actionCategory === 'Register' || actionCategory === 'Schema' || actionCategory === 'Data';

      // Different descriptions based on whether it's register-level or row-level
      const actions = {
        Create: isRegisterLevel ? 'created this register' : 'created this row',
        Update: isRegisterLevel
          ? (count === 1 ? `updated the register ${this.singleFieldName}` : `updated ${count} register fields`)
          : (count === 1 ? `updated the ${this.singleFieldName}` : `updated ${count} fields`),
        Delete: isRegisterLevel ? 'deleted this register' : 'deleted this row',
        Import: `imported ${this.log.affectedRowCount || ''} rows`.trim(),
        Export: `exported ${this.log.affectedRowCount || 'the'} rows`,
        SchemaChange: 'modified the schema',
        UserAssignment: 'was assigned to this register',
        UserUnassignment: 'was removed from this register',
        ClearData: `cleared ${this.log.affectedRowCount || 'all'} rows`
      };

      return actions[actionType] || `performed ${actionType?.toLowerCase() || 'an action'}`;
    },
    singleFieldName() {
      if (this.log.changes?.[0]) {
        return this.log.changes[0].displayName || this.log.changes[0].fieldName;
      }
      if (this.log.summary) {
        const match = this.log.summary.match(/Updated \d+ field\(s\): (.+)/);
        if (match) {
          const fields = match[1].split(', ');
          if (fields.length === 1) return fields[0];
        }
      }
      return 'a field';
    },

    // Formatted timestamp
    formattedTimestamp() {
      const date = new Date(this.log.actionTimestamp);
      const month = date.toLocaleString('en-US', { month: 'long' });
      const day = date.getDate();
      const year = date.getFullYear();
      const time = date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      return `${month} ${day}, ${year} at ${time}`;
    },

    // Show field names list when we only have summary (no detailed changes)
    showFieldsList() {
      return this.log.actionType === 'Update' &&
             this.log.changeCount > 0 &&
             this.changedFieldNames.length > 0;
    },
    changedFieldNames() {
      if (this.log.summary) {
        const match = this.log.summary.match(/Updated \d+ field\(s\): (.+)/);
        if (match) {
          return match[1].split(', ');
        }
      }
      return [];
    },

    canViewDetails() {
      const totalChanges = this.log.changeCount || this.log.changes?.length || 0;
      return this.log.actionType === 'Update' && totalChanges > 0;
    }
  },
  methods: {
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'Empty';
      }
      const str = String(value);
      return str.length > 50 ? str.slice(0, 50) + '…' : str;
    },

    async toggleExpand() {
      if (this.isExpanded) {
        // Collapse
        this.isExpanded = false;
        return;
      }

      // Expand and fetch details if not already loaded
      if (this.detailedChanges.length === 0) {
        this.loadingDetail = true;
        try {
          const detail = await registerService.getAuditLogDetail(this.log.id);
          this.detailedChanges = detail.changes || [];
        } catch (error) {
          error('Failed to load audit detail:', error);
          this.detailedChanges = [];
        } finally {
          this.loadingDetail = false;
        }
      }
      this.isExpanded = true;
    }
  }
};
</script>

<style scoped>
/* ============================================
   AUDIT LOG ITEM
   Refined activity feed inspired by Jira
   With inline expandable diff view
   ============================================ */

.audit-item {
  display: flex;
  gap: 12px;
  padding: 16px 4px;
  transition: background 0.15s ease;
}

.audit-item.is-expanded {
  background: var(--v-bg3-base);
  margin: 0 -4px;
  padding: 16px 8px;
  border-radius: 8px;
}

.audit-item + .audit-item {
  border-top: 1px solid var(--ds-border-default);
}

.audit-item.is-expanded + .audit-item {
  border-top: none;
}

/* Avatar */
.audit-avatar {
  flex-shrink: 0;
  padding-top: 2px;
}

.avatar-circle {
  box-shadow: 0 0 0 2px var(--v-bg2-base);
}

.avatar-text {
  font-size: 11px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.02em;
}

/* Body */
.audit-body {
  flex: 1;
  min-width: 0;
}

/* Header */
.audit-header {
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 2px;
}

.audit-user {
  font-weight: 700;
  color: var(--v-text-base);
}

.audit-action {
  color: var(--v-text-base);
  margin-left: 4px;
}

/* Timestamp */
.audit-time {
  display: block;
  font-size: 12px;
  color: var(--v-text_secondary-base);
  margin-bottom: 2px;
}

/* Field Names List (collapsed view) */
.audit-fields-list {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.field-tag {
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  color: var(--v-text-base);
  background: #ff99001a;
  border-radius: 4px;
  border: 1px solid var(--ds-border-default);
}

/* Expand/Collapse Button */
.audit-details-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 0;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: var(--v-primary-base);
  cursor: pointer;
  transition: all 0.15s ease;
}

.audit-details-btn:hover {
  color: var(--v-primary-darken1);
}

.audit-details-btn:hover span {
  text-decoration: underline;
}

.audit-details-btn svg {
  transition: transform 0.2s ease;
}

.audit-details-btn svg.rotated {
  transform: rotate(90deg);
}

/* Expanded Detail View */
.audit-detail-expanded {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Change Card - Diff Style */
.change-card {
  background: var(--v-bg2-base);
  border: 1px solid var(--ds-border-default);
  border-radius: 6px;
  overflow: hidden;
}

.change-field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: var(--v-bg3-base);
  border-bottom: 1px solid var(--ds-border-subtle);
}

.field-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--v-text-base);
}

.field-type {
  font-size: 10px;
  color: var(--v-text_secondary-base);
  padding: 1px 5px;
  background: var(--v-bg2-base);
  border-radius: 3px;
  font-family: 'Roboto Mono', monospace;
}

/* Value Rows - Diff Style */
.change-values {
  padding: 0;
}

.value-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 10px;
  font-size: 13px;
  line-height: 1.4;
}

.value-row + .value-row {
  border-top: 1px solid var(--ds-border-subtle);
}

.value-old {
  background: rgba(239, 68, 68, 0.06);
}

.value-new {
  background: rgba(16, 185, 129, 0.06);
}

.value-indicator {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
  margin-top: 1px;
}

.value-old .value-indicator {
  background: #FEE2E2;
  color: #DC2626;
}

.value-new .value-indicator {
  background: #D1FAE5;
  color: #059669;
}

.value-content {
  flex: 1;
  word-break: break-word;
  color: var(--v-text-base);
}

.value-old .value-content {
  color: #991B1B;
}

.value-new .value-content {
  color: #065F46;
  font-weight: 500;
}

/* No Changes Message */
.no-changes-msg {
  margin-top: 10px;
  padding: 12px;
  font-size: 12px;
  color: var(--v-text_secondary-base);
  background: var(--v-bg2-base);
  border-radius: 6px;
  text-align: center;
}

/* Expand Transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 500px;
  opacity: 1;
}

/* Dark mode adjustments for diff colors */
.theme--dark .value-old {
  background: rgba(239, 68, 68, 0.12);
}

.theme--dark .value-new {
  background: rgba(16, 185, 129, 0.12);
}

.theme--dark .value-old .value-indicator {
  background: rgba(220, 38, 38, 0.3);
  color: #FCA5A5;
}

.theme--dark .value-new .value-indicator {
  background: rgba(5, 150, 105, 0.3);
  color: #6EE7B7;
}

.theme--dark .value-old .value-content {
  color: #FCA5A5;
}

.theme--dark .value-new .value-content {
  color: #6EE7B7;
}
</style>
